import React, {
  useState,
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
} from 'react'
import { useTranslation } from 'i18n'
import IntersectionVisible from 'components/IntersectionVisible'

import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import Card from 'components/index/Card'
import LinkCards from 'components/index/LinkCards'
import LinkCard from 'components/index/LinkCard'
import FacebookCommentsSection from 'components/FacebookCommentsSection'

import {
  retrieveCompletedBreaksFromLocalOrAPI,
  getFeaturedBreaks,
  getPointsForBreak,
} from 'services/break'
import { userTypeEnum } from 'services/user'
import { titleParser } from 'util/titleParser'

import './CompleteStep3.scss'


const SliderComponent = forwardRef((props, ref) => {
  var sliderRef = {}
  var settings = {
    dots: true,
    arrows: true,
    infinite: false,
    speed: 500,
    variableWidth: true,
    dotsClass: 'slick-dots card__slider-dots',
    slidesToShow: 3,
    slidesToScroll: 3,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 450,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      // You can unslick at a given breakpoint now by adding:
      // settings: "unslick"
      // instead of a settings object
    ],
  }

  useImperativeHandle(ref, () => ({
    GotoSlick() {
      sliderRef.slickGoTo(0)
    },
  }))

  return props.children ? (
    <Slider
      className={'card__slider'}
      ref={slider => (sliderRef = slider)}
      {...settings}
    >
      {props.children.map((obj, index) => {
        return <React.Fragment key={index}>{obj}</React.Fragment>
      })}
    </Slider>
  ) : (
    <></>
  )
})

const usePrevious = (value, initialValue) => {
  const ref = useRef(initialValue)
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const useEffectDebugger = (effectHook, dependencies, dependencyNames = []) => {
  const previousDeps = usePrevious(dependencies, [])

  const changedDeps = dependencies.reduce((accum, dependency, index) => {
    if (dependency !== previousDeps[index]) {
      const keyName = dependencyNames[index] || index
      return {
        ...accum,
        [keyName]: {
          before: previousDeps[index],
          after: dependency,
        },
      }
    }

    return accum
  }, {})

  if (Object.keys(changedDeps).length) {
    console.log('[use-effect-debugger] ', changedDeps)
  }

  useEffect(effectHook, dependencies)
}

const CompleteStep3 = ({
  brk,
  isEmbed,
  breakId,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  userType,
  service,
}) => {
  const { t } = useTranslation('complete')
  const [awardedPoints, setAwardedPoints] = useState(0)
  const [featuredBreaks, setFeaturedBreaks] = useState([])
  const [completedBreaks, setCompletedBreaks] = useState([])
  const childRef = useRef()
  const linkCards = step && step.linkCards

  const handleIsOnScreen = async isIntersecting => {
    if (isIntersecting) {
      onEngage()
      await onAwardPoints()

      const awardedPts = await getPointsForBreak(breakId)
      setAwardedPoints(awardedPts.detail)
    }
  }

  // onload, get points so far -- will not include the points they get for visiting this step the first time
  useEffect(() => {
    const asyncWrapper = async () => {
      if (userType !== userTypeEnum.NONE) {
        try {
          const response = await getPointsForBreak()
          setFeaturedBreaks(response.detail)
        } catch (e) {}
      }
    }
    asyncWrapper()
  }, [setAwardedPoints, userType])

  // useEffectDebugger(() => {
  useEffect(() => {
    const asyncWrapper = async () => {
      try {
        const response = await getFeaturedBreaks({ limit: 7 })
        setFeaturedBreaks(response.detail)
        const completedBrks = await retrieveCompletedBreaksFromLocalOrAPI(
          response.detail,
        )
        setCompletedBreaks(completedBrks)
      } catch (e) {
        // a 401 can get thrown for guests
        // console.log(e)
      }
    }

    if (!isEmbed) {
      asyncWrapper()
    }
  }, [isEmbed, getFeaturedBreaks, setFeaturedBreaks])

  const breakIsCompleted = breakId => {
    return completedBreaks && completedBreaks[breakId]
  }

  return (
    <section className={`complete-step`}>
      <div
        className={`step-content step-content-100 ${
          brk?.navigationType === 'TOP' ? 'top-navigation' : ''
        }`}
      >
        <IntersectionVisible
          active={true}
          options={{
            threshold: 0.4,
          }}
          onIntersect={handleIsOnScreen}
        >
          <div className="container">
            <div className="content">
              <h2
                className="complete-title step-content__title complete-step-headline"
                dangerouslySetInnerHTML={{
                  __html: `${titleParser(step.headline || t('defaultCongrats'))}`,
                }}
              />

              {!isEmbed && (
                <h2
                  className={`complete-title subcopy`}
                  dangerouslySetInnerHTML={{
                    __html: ` ${t('pointsEarned', { count: awardedPoints })}`,
                  }}
                />
              )}

              {step.introCopy && (
                <h2
                  className={`complete-title`}
                  dangerouslySetInnerHTML={{ __html: step.introCopy }}
                />
              )}

              {step.description && (
                <h3
                  className={`complete-title complete-step-copy`}
                  dangerouslySetInnerHTML={{ __html: step.description }}
                />
              )}
            </div>

            <div className="card__slider">
              {linkCards && linkCards.length === 1 ? (
                <LinkCard card={linkCards[0]} />
              ) : (
                <SliderComponent className="card__slider" ref={childRef}>
                  {linkCards && linkCards.length !== 0
                    ? linkCards.map(item => {
                        return (
                          <LinkCards
                            title={item.title}
                            imageId={item.imageId}
                            buttonCopy={item.buttonCopy}
                            buttonUrl={item.url}
                          />
                        )
                      })
                    : featuredBreaks &&
                      featuredBreaks.map((brk, index) => {
                        return (
                          <Card
                            brk={brk}
                            key={index}
                            thumbnail={false}
                            breakIsCompleted={breakIsCompleted(brk.id)}
                          ></Card>
                        )
                      })}
                </SliderComponent>
              )}
            </div>
          </div>

          {brk.facebookEnabled && (
            <FacebookCommentsSection
              question={step.commentQuestion || ''}
              editorial={false}
            />
          )}
        </IntersectionVisible>
      </div>
    </section>
  )
}

export default CompleteStep3
